import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * Copies the product URL and shows an inverted tooltip; syncs with variant section HTML.
 */
class ProductShareLinkComponent extends Component {
  /** @type {HTMLElement | null} */
  #section = null;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #hideTimer;

  connectedCallback() {
    super.connectedCallback();
    this.#section = this.closest('.shopify-section, dialog');
    this.#section?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
  }

  disconnectedCallback() {
    this.#section?.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    this.#section = null;
    clearTimeout(this.#hideTimer);
    super.disconnectedCallback();
  }

  /** @param {Event} e */
  #onVariantUpdate = (e) => {
    if (!(e instanceof CustomEvent) || !e.detail?.data?.html) return;
    const { data } = e.detail;

    if (data.newProduct) {
      this.dataset.productId = data.newProduct.id;
    } else if (e.target instanceof HTMLElement && e.target.dataset.productId && e.target.dataset.productId !== this.dataset.productId) {
      if (!data.newProduct) return;
    }

    const next = data.html.querySelector(`product-share-link-component[data-block-id="${this.dataset.blockId}"]`);
    if (!next) {
      this.remove();
      return;
    }
    this.replaceWith(next);
  };

  /**
   * Ensures clipboard gets a full https URL (handles relative paths in data-share-url).
   * @param {string} raw
   */
  #absoluteShareUrl(raw) {
    const href = (raw ?? '').trim();
    if (!href) return '';
    if (/^https?:\/\//i.test(href)) return href;
    try {
      const base = window.location?.origin || '';
      if (!base) return href;
      return new URL(href.startsWith('/') ? href : `/${href}`, base).href;
    } catch {
      return href;
    }
  }

  handleShare = () => {
    const url = this.#absoluteShareUrl(this.dataset.shareUrl ?? '');
    if (!url) return;

    const tip = this.refs.tooltip;

    const showTip = () => {
      if (tip instanceof HTMLElement) {
        tip.hidden = false;
        clearTimeout(this.#hideTimer);
        this.#hideTimer = setTimeout(() => {
          tip.hidden = true;
        }, 2500);
      }
    };

    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(url).then(showTip).catch(() => {
        if (tip instanceof HTMLElement) tip.hidden = true;
      });
    }
  };
}

if (!customElements.get('product-share-link-component')) {
  customElements.define('product-share-link-component', ProductShareLinkComponent);
}
